import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { HStack, VStack } from "../../components/utils";
import { tableEntryStyle } from "../../components/TableComponent";
import { limitString } from "../../utils/string";
import { formatToReadableNumber } from "../../utils/currencyRoboto";
import Avatar from "../Utility/Avatar";
import CaptableHoverCard from "./CaptableHoverCard";
import { CaptableTag } from "../../components/AllotmentTag";
import RoundTag from "../../shared/RoundTag";
import { checkVoid } from "../../utils/transactionUtils";
import { formatDisplayDate } from "../../utils/date";

export function shareholderNameRender(data: any) {
  const isShareholderRow =
    (data.node.level === 1 && !data.data?.groupName) || data.node.level === 2;
  const isGroupRow = data.node.level === 0;
  const isGroupNameRow = data.node.level === 1 && data.data?.groupName;
  const isTotalRow = data.value === "Total";

  return (
    <td className="flex flex-col pb-2 text-sm">
      <div
        className={`flex items-center w-full ${
          isGroupNameRow ? "text-center" : "text-left"
        }`}
      >
        {isShareholderRow && !isTotalRow && (
          <div className="pt-3 pr-2">
            <Avatar name={data.value || ""} />
          </div>
        )}

        <div
          className={`${
            isTotalRow || isGroupRow || isGroupNameRow
              ? "pt-2 cursor-default"
              : "ml-2 cursor-pointer"
          } ${isGroupNameRow ? "w-full text-center " : ""}`}
        >
          {isGroupNameRow && (
            <p className="font-semibold text-xs2 text-[#464E5F] pt-1">
              {limitString(data.value, 24)}
            </p>
          )}

          {isShareholderRow && !isTotalRow && (
            <>
              <CaptableHoverCard shareholder={data.data} />
              <p className="font-semibold pt-1 text-xs2 text-[#464E5F]">
                {limitString(
                  data.value?.toLowerCase() === "esop pool"
                    ? "ESOP Pool"
                    : data.value || "",
                  24
                )}
              </p>
              <CaptableTag
                tagName={data.data.shareholderName?.type ?? "Others"}
                dataEventName={"captableShareholderTypeDrilling"}
              />
            </>
          )}

          {(isTotalRow || isGroupRow) && (
            <p className="font-semibold pt-1 text-xs2 text-[#464E5F]">
              {data.value}
            </p>
          )}
        </div>
      </div>
    </td>
  );
}

export function stakeholderNameRender(data: any) {
  const { onClickShareholderName } = data;
  const shareholder = data.data.shareholderData || data.data;
  const isTotalRow = data.data.stakeholderName === "Total";

  return (
    <td className="flex flex-col pb-2 text-sm">
      <div
        className="flex items-center w-full text-left cursor-pointer"
        onClick={() => onClickShareholderName?.()}
      >
        {!isTotalRow && (
          <div className="pt-3 pr-2">
            <Avatar name={data.data.stakeholderName || ""} />
          </div>
        )}
        {!isTotalRow ? (
          <div className="ml-2">
            <CaptableHoverCard shareholder={data.data} />
            <p className="font-semibold pt-1 text-xs2 text-[#464E5F]">
              {limitString(
                data.value?.toLowerCase() === "esop pool"
                  ? "ESOP Pool"
                  : data.data.stakeholderName || "",
                24
              )}
            </p>
            <CaptableTag
              tagName={shareholder?.type ?? "Others"}
              dataEventName="captableShareholderTypeDrilling"
            />
          </div>
        ) : (
          <p className="flex justify-center items-center pt-2 text-left font-semibold text-xs-2">
            Total
          </p>
        )}
      </div>
    </td>
  );
}

export function EquitySharesRender(data: any) {
  return (
    <VStack>
      <div>
        <div className="pt-3 whitespace-nowrap">
          <p className={tableEntryStyle}>
            {formatToReadableNumber({
              value: Number(data.data?.equityShares),
              format: data.data.currencyType,
            })}
          </p>
        </div>
        <div>
          {data.data?.isEquityPartlyPaid && data.node.level > 0 && (
            <div className="px-1 mt-2 mb-2 text-red-500 border border-red-500 rounded-sm font-xxs w-fit">
              Incl. Partly Paid
            </div>
          )}
        </div>
      </div>
    </VStack>
  );
}

export function DebenturesRender(data: any) {
  return (
    <VStack>
      <div className="pt-3 whitespace-nowrap">
        <p className={tableEntryStyle}>
          {formatToReadableNumber({
            value: Number(data.data?.debentures),
            format: data.data.currencyType,
          })}
        </p>
      </div>
      <div className="pb-1">
        <HStack className="gap-2">
          {data.data?.isDebtPartlyPaid && data.node.level > 0 && (
            <div className="px-1 mt-2 mb-2 text-red-500 border border-red-500 rounded-sm font-xxs w-fit">
              Incl. Partly Paid
            </div>
          )}
          {data.data?.isdebt && data.node.level > 0 && (
            <div className="px-1 mb-2 text-red-500 border border-red-500 rounded-sm font-xxs w-fit">
              Debt
            </div>
          )}
        </HStack>
      </div>
    </VStack>
  );
}

export function RoundsRender(data: any) {
  const navigate = useNavigate();
  const onClickNavigate = (url: string) => navigate(url);
  return (
    <div className="pt-3 whitespace-nowrap">
      {data.node.level === 0 || data.data?.isGroup ? (
        data.node.key &&
        data.data?.roundIdentifiers[
          data.node.key.toLowerCase().split(" ").join("")
        ]?.length > 0 ? (
          <RoundTag
            roundNames={
              data.data?.roundIdentifiers[
                data.node.key.toLowerCase().split(" ").join("")
              ] || []
            }
            navigate={data.data.cin ? () => {} : onClickNavigate}
          />
        ) : (
          <p></p>
        )
      ) : (
        <RoundTag
          roundNames={data.data.rounds || []}
          navigate={data.data.cin ? () => {} : onClickNavigate}
        />
      )}
    </div>
  );
}

export function PreferenceShareRender(data: any) {
  return (
    <VStack>
      <div className="pt-3 whitespace-nowrap">
        <p className={tableEntryStyle}>
          {formatToReadableNumber({
            value: Number(data.data?.prefShares),
            format: data.data.currencyType,
          })}
        </p>
      </div>
      <div>
        {data.data?.isPreferencePartlyPaid && data.node.level > 0 && (
          <div className="px-1 mb-2 text-red-500 border border-red-500 rounded-sm font-xxs w-fit ">
            Incl. Partly Paid
          </div>
        )}
      </div>
    </VStack>
  );
}

export function AmountInvestedRender(data: any) {
  return (
    <div className="pt-3 whitespace-nowrap">
      <p className={tableEntryStyle}>
        {formatToReadableNumber({
          value: data.data?.amountInvested,
          format: data.data.currencyType,
        })}
      </p>
    </div>
  );
}

export function TotalInvestedAmountRender(data: any) {
  return (
    <div className="pt-3 whitespace-nowrap">
      <p className={tableEntryStyle}>
        {formatToReadableNumber({
          value: data.data?.totalAmount,
          format: data.data.currencyType,
        })}
      </p>
    </div>
  );
}

export function FdbSharesRender(data: any) {
  return (
    <div className="pt-3 whitespace-nowrap">
      <p className={tableEntryStyle}>
        {formatToReadableNumber({
          value: data.data?.fdbShares,
          format: data.data.currencyType,
        })}
      </p>
    </div>
  );
}

export function FdbPercentageRender(data: any) {
  return (
    <div className="pt-3 whitespace-nowrap">
      <p className={tableEntryStyle}>
        {formatToReadableNumber({
          value: data.data?.fdbPercentage || 0,
          format: data.data.currencyType,
        })}
      </p>
    </div>
  );
}

export function WarrantsRender(data: any) {
  return (
    <div className="pt-3 whitespace-nowrap">
      <p className={tableEntryStyle}>
        {formatToReadableNumber({
          value: Number(data.data?.warrants),
          format: data.data.currencyType,
        })}
      </p>
    </div>
  );
}

export function NotesRender(data: any) {
  return (
    <div className="pt-3 whitespace-nowrap">
      <p className={tableEntryStyle}>
        {formatToReadableNumber({
          value: data.data?.notes,
          format: data.data.currencyType,
        })}
      </p>
    </div>
  );
}

export function OptionsRender(data: any) {
  return (
    <div className="pt-3 whitespace-nowrap">
      <p className={tableEntryStyle}>
        {formatToReadableNumber({
          value: Number(data.data?.options),
          format: data.data.currencyType,
        })}
      </p>
    </div>
  );
}

export function StakeholderNotesRender(data: any) {
  return (
    <div className="pt-3 whitespace-normal break-words max-w-xs">
      <p className={tableEntryStyle}>{data.data?.notes || ""}</p>
    </div>
  );
}

export function AllotmentDateRender(data: any) {
  return (
    <div className="pt-3 whitespace-nowrap">
      <p className={tableEntryStyle}>
        {formatDisplayDate(data.data?.allotmentDate)}
      </p>
    </div>
  );
}
